import moment from 'moment-timezone';
import { store } from 'store';
import { Option } from 'types/Generics';
import { Marketplace } from 'types/Marketplace';
import { Pet, PetSize, PetType, PetTypeId, PetTypeIds, PetVaccine } from 'types/Pet';

export const requiredVaccines = (
    marketplace: Marketplace,
    petTypeId?: PetTypeId
): Array<Option<string>> => (petTypeId ? marketplace.vaccinesByType[petTypeId] ?? [] : []);

export const getPetSizeByWeight = (
    weight: number,
    petType: PetTypeId,
    orderedSizes: Array<PetSize>
): PetSize | null => {
    if (petType === PetTypeIds.CAT) {
        return {
            id: 'small',
            name: 'Small',
            description: 'Small',
            minWeight: null,
            maxWeight: null
        };
    }

    if (petType === PetTypeIds.EXOTIC) {
        return {
            id: 'medium',
            name: 'Medium',
            description: 'Medium',
            minWeight: null,
            maxWeight: null
        };
    }

    let size = orderedSizes.find(
        size =>
            (size.minWeight === null || weight >= size.minWeight) &&
            (size.maxWeight === null || weight <= size.maxWeight)
    );

    if (size === undefined && orderedSizes.length > 0) {
        const [firstSize] = orderedSizes;
        const [lastSize] = orderedSizes.reverse();

        if (firstSize.minWeight === null || weight < firstSize.minWeight) {
            size = firstSize;
        }

        if (lastSize.minWeight === null || weight > lastSize.minWeight) {
            size = lastSize;
        }
    }

    return size ?? null;
};

export const getPetBreedName = (petType: PetType, breedId: string | number): string => {
    const masterData = store.getState().masterData.data;
    const petBreed = masterData.petBreeds.find(
        breed => breed.id === breedId && breed.filter === petType.id
    );

    if (!petBreed) return '';

    return petBreed.name;
};

export const petsMatch = (pet1: Pet, pet2: Pet) => {
    const namesMatch = pet1.name.toLowerCase() === pet2.name.toLowerCase();
    const typesMatch = pet1.type.id === pet2.type.id;
    const breedsMatch = pet1.breed && pet2.breed ? pet1.breed.id === pet2.breed.id : true;

    return namesMatch && typesMatch && breedsMatch;
};

export interface VaccineIssues {
    /** Required vaccines with no record or no expiration date. */
    missing: Array<Option<string>>;
    /** Required vaccines present but with an expiration date in the past. */
    expired: Array<Option<string>>;
}

/**
 * Compares a pet's vaccine records against the marketplace's required vaccines.
 * A required vaccine is considered valid only when it is present AND not expired.
 * Validation is based on the `expiration` date (not the `status` string) to avoid
 * the casing inconsistencies between the backend and the form.
 */
export const getVaccineIssues = (
    vaccines: Array<PetVaccine>,
    requiredVaccines: Array<Option<string>>
): VaccineIssues => {
    const missing: Array<Option<string>> = [];
    const expired: Array<Option<string>> = [];

    requiredVaccines.forEach(required => {
        const record = vaccines.find(vaccine => vaccine.type?.id === required.id);

        if (!record || !record.expiration) {
            missing.push(required);
            return;
        }

        if (record.expiration.isBefore(moment())) {
            expired.push(required);
        }
    });

    return { missing, expired };
};

export const hasVaccineIssues = (issues: VaccineIssues): boolean =>
    issues.missing.length > 0 || issues.expired.length > 0;
